import React, { useCallback } from 'react';
import { useVesonUpdateDataMutation } from '../../graphql';
import { hasFeature } from '../../state/zustand';
import { usePortCallUpdates } from '../PortCallUpdates';
import { toast } from 'react-toastify';
import { Button } from '../ui';
import { ApolloError } from '@apollo/client';
import { handleToastOfScenarioUpdate, handleToastOfSyncData } from './helpers';
import { useSharedNotificationCenter } from '../contexts/NotificationsContext';

export function VesonUpdates({
  restoreIntegrationScenario,
}: {
  restoreIntegrationScenario: () => void;
}): JSX.Element | null {
  const { add } = useSharedNotificationCenter();
  const isVesonToolsFeatureAvailable = hasFeature('veson_tools');
  const { getPortCallUpdates } = usePortCallUpdates();

  const [vesonUpdateDataMutation] = useVesonUpdateDataMutation();
  const vesonUpdateData = useCallback(() => {
    const toastId = toast(`Updating IMOS data...`, {
      type: toast.TYPE.INFO,
      autoClose: false,
      isLoading: true,
    });

    vesonUpdateDataMutation({
      fetchPolicy: 'no-cache',
    })
      .then((updateVesonResult) => {
        const result = updateVesonResult.data?.vesonUpdateData;
        if (!result) {
          throw new Error(`No data`);
        }

        const { syncOrder, syncVessel, updateScenario } = result;

        handleToastOfSyncData('IMOS', 'orders', syncOrder);
        handleToastOfSyncData('IMOS', 'vessels', syncVessel);
        handleToastOfScenarioUpdate('IMOS', updateScenario, add, toastId);

        toast.dismiss(toastId);
      })
      .then(() => getPortCallUpdates)
      .then(() => restoreIntegrationScenario())
      .catch((err) => {
        let errorMessage = 'Error processing IMOS data! Please try again';
        if (err instanceof ApolloError) {
          errorMessage = err.message;
          if (err.networkError) {
            errorMessage = `Update Failed. ${errorMessage}. Please try again`;
          }
        }
        toast.update(toastId, {
          render: errorMessage,
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 3000,
        });
      });
  }, [
    vesonUpdateDataMutation,
    add,
    getPortCallUpdates,
    restoreIntegrationScenario,
  ]);

  if (!isVesonToolsFeatureAvailable) return null;

  return (
    <Button
      data-testid="refresh-veson-data"
      mr={3}
      variant="dark"
      onPress={vesonUpdateData}
    >
      Refresh Veson Data
    </Button>
  );
}
